import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  SpecialText,
  Title,
  Heading,
  Text,
  Technologies,
  Technology,
  TechnologyName,
  HeroBackground,
  PointerGlow,
  HeroContent,
} from "./Intro.Styled";
import { motion } from "framer-motion";
import {
  SiFigma,
  SiFirebase,
  SiHtml5,
  SiJavascript,
  SiNextdotjs,
  SiNuxtdotjs,
  SiReact,
  SiTypescript,
  SiVuedotjs,
} from "react-icons/si";

const technologies = [
  { name: "Next.js", icon: SiNextdotjs },
  { name: "React Native", icon: SiReact },
  { name: "TypeScript", icon: SiTypescript },
  { name: "JavaScript", icon: SiJavascript },
  { name: "HTML", icon: SiHtml5 },
  { name: "Vue", icon: SiVuedotjs },
  { name: "Nuxt", icon: SiNuxtdotjs },
  { name: "Firebase", icon: SiFirebase },
  { name: "Figma", icon: SiFigma },
];

const technologyListVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.045, delayChildren: 0.55 },
  },
};

const technologyVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  },
};

const Intro = () => {
  const heroBackgroundRef = useRef(null);
  const [selectedTechnology, setSelectedTechnology] = useState(null);
  const [hoveredTechnology, setHoveredTechnology] = useState(null);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const background = heroBackgroundRef.current;
      if (event.pointerType === "touch" || !background) return;

      const heroBounds = background.getBoundingClientRect();
      const isInsideHero =
        event.clientY >= heroBounds.top && event.clientY <= heroBounds.bottom;

      if (!isInsideHero) {
        background.style.setProperty("--pointer-opacity", "0.18");
        return;
      }

      const x = ((event.clientX - heroBounds.left) / heroBounds.width) * 100;
      const y = ((event.clientY - heroBounds.top) / heroBounds.height) * 100;

      background.style.setProperty("--pointer-x", `${x}%`);
      background.style.setProperty("--pointer-y", `${y}%`);
      background.style.setProperty("--pointer-opacity", "0.78");
    };

    const handlePointerLeave = () =>
      heroBackgroundRef.current?.style.setProperty(
        "--pointer-opacity",
        "0.18"
      );

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", handlePointerLeave);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      document.documentElement.removeEventListener(
        "mouseleave",
        handlePointerLeave
      );
    };
  }, []);

  return (
    <Container
      as={motion.section}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <HeroBackground ref={heroBackgroundRef} aria-hidden="true">
        <span />
        <span />
        <span />
        <PointerGlow />
      </HeroBackground>
      <HeroContent>
        <SpecialText>Independent front-end engineer</SpecialText>
        <Title>Viet Hoang Nguyen</Title>
        <Heading>I build dependable web and mobile products.</Heading>
        <Text>
          I partner with startups and product teams from early product decisions
          through production, taking ownership of responsive interfaces, UX
          details, and front-end quality.
        </Text>
        <Technologies
          as={motion.div}
          variants={technologyListVariants}
          initial="hidden"
          animate="visible"
          aria-label="Core technologies"
        >
          {technologies.map(({ name, icon: Icon }) => (
            <Technology
              as={motion.button}
              type="button"
              variants={technologyVariants}
              whileHover={{ y: -3 }}
              key={name}
              title={name}
              aria-label={name}
              aria-pressed={selectedTechnology === name}
              $active={selectedTechnology === name}
              onMouseEnter={() => setHoveredTechnology(name)}
              onMouseLeave={() => setHoveredTechnology(null)}
              onFocus={() => setHoveredTechnology(name)}
              onBlur={() => setHoveredTechnology(null)}
              onClick={() =>
                setSelectedTechnology((selected) =>
                  selected === name ? null : name
                )
              }
            >
              <Icon aria-hidden="true" />
            </Technology>
          ))}
        </Technologies>
        <TechnologyName aria-live="polite">
          {hoveredTechnology || selectedTechnology || ""}
        </TechnologyName>
      </HeroContent>
    </Container>
  );
};

export default Intro;
